import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import partnerApi, { errMsg } from '../api';
import { usePartnerAuth } from '../auth';

export default function Login() {
  const [step, setStep] = useState('phone'); // 'phone' | 'code'
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { refresh } = usePartnerAuth();

  const sendCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await partnerApi.post('/auth/send-otp', { phone });
      setStep('code');
      toast.success('Code sent to your mobile');
    } catch (err) {
      toast.error(errMsg(err, 'Could not send code'));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await partnerApi.post('/auth/verify-otp', { phone, code });
      localStorage.setItem('partner_mahi_token', data.token);
      await refresh();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(errMsg(err, 'Invalid or expired code'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface-page px-4">
      <div className="mb-8 text-center">
        <span className="font-serif text-2xl text-ink">
          <span className="text-raspberry-600">mahi</span>.fashion
        </span>
        <p className="mt-1 text-sm text-ink-secondary">Partner Portal</p>
      </div>

      <div className="w-full max-w-sm rounded-2xl border border-hairline bg-surface p-8 shadow-sm">
        <h1 className="mb-1 text-base font-semibold text-ink">Sign in</h1>
        <p className="mb-6 text-sm text-ink-secondary">
          Enter the mobile number registered with your partner account.
        </p>

        {step === 'phone' ? (
          <form onSubmit={sendCode} className="space-y-4">
            <label className="block text-sm font-medium text-ink-secondary">
              Mobile number
              <input
                type="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="9876543210"
                className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-raspberry-300"
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-lg bg-raspberry-600 py-2.5 text-sm font-medium text-white transition-colors hover:bg-raspberry-700 disabled:opacity-50"
            >
              {busy ? 'Sending…' : 'Send code'}
            </button>
          </form>
        ) : (
          <form onSubmit={verify} className="space-y-4">
            <label className="block text-sm font-medium text-ink-secondary">
              Code sent to {phone}
              <input
                type="text"
                inputMode="numeric"
                required
                autoFocus
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="000000"
                className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-center text-lg tracking-[0.4em] text-ink focus:outline-none focus:ring-2 focus:ring-raspberry-300"
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-lg bg-raspberry-600 py-2.5 text-sm font-medium text-white transition-colors hover:bg-raspberry-700 disabled:opacity-50"
            >
              {busy ? 'Verifying…' : 'Sign in'}
            </button>
            <button
              type="button"
              onClick={() => { setStep('phone'); setCode(''); }}
              className="w-full text-xs text-ink-muted hover:text-ink-secondary"
            >
              Use a different number
            </button>
          </form>
        )}

        <div className="mt-6 border-t border-hairline pt-5 text-center">
          <p className="text-xs text-ink-muted">
            New to mahi.fashion?{' '}
            <Link to="/register" className="font-medium text-raspberry-600 hover:text-raspberry-700">
              Apply as a brand partner
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
